import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Lock, Pencil, Plus, Trash2 } from 'lucide-react';

import Button from '@/shared/components/Button';
import Collapse from '@/shared/components/Collapse';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import Input from '@/shared/components/Input';
import MultiSelect from '@/shared/components/MultiSelect';
import Select from '@/shared/components/Select';
import Textarea from '@/shared/components/Textarea';
import ToggleSwitch from '@/shared/components/ToggleSwitch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/dialog';

import { ENTITY_NAME_BACK_OFFICE_INSTRUCTION_BLOCK } from '../../../dao/dbConstants';
import { searchEntityRecords } from '../../../utils/entityApiUtils';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';

const API_BASE = `${SERVER_URL}/api/ui/bo/instruction-blocks`;

const saveBlockRequest = async (payload, id = null) => {
  const res = await fetch(id ? `${API_BASE}/${id}` : API_BASE, {
    method: id ? 'PUT' : 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(errorBody?.message || 'Failed to save instruction block.');
  }
  const body = await res.json();
  return body.item;
};

const deleteBlockRequest = async (id) => {
  const res = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(errorBody?.message || 'Failed to delete instruction block.');
  }
};

const CORE_BLOCKS = [
  {
    id: 'core-brand-voice',
    name: 'brand-voice',
    description: 'Tone, style and voice rules applied to all generated content',
    content:
      'Write in a professional yet approachable tone. Avoid jargon unless the audience expects it. Keep sentences concise and active. Never overpromise or use superlatives without support.',
  },
  {
    id: 'core-output-format',
    name: 'output-format',
    description: 'Structural and formatting rules for generated output',
    content:
      'Structure output with clear headings and short paragraphs. Use bullet lists for enumerations. Do not include markdown syntax the target channel cannot render.',
  },
  {
    id: 'core-compliance-guardrails',
    name: 'compliance-guardrails',
    description: 'Legal and safety guardrails that always apply',
    content:
      'Never fabricate facts, prices, availability or legal claims. Do not generate discriminatory or misleading content. Flag uncertain statements instead of asserting them.',
  },
];

export const referencesVertical = (block, verticalId) => {
  const groups = block.scopeRules ?? [];
  return groups.some((group) =>
    (group ?? []).some((condition) => {
      if (condition.field !== 'vertical') return false;
      const values = Array.isArray(condition.value)
        ? condition.value.map(Number)
        : [Number(condition.value)];
      if (condition.operator === 'not_equals') return !values.includes(Number(verticalId));
      return values.includes(Number(verticalId));
    }),
  );
};

export const hasVerticalRule = (block) =>
  (block.scopeRules ?? []).some((group) =>
    (group ?? []).some((condition) => condition.field === 'vertical'),
  );

const getAppliedTo = (block) => {
  for (const group of block.scopeRules ?? []) {
    for (const condition of group ?? []) {
      if (condition.field === 'vertical') {
        const ids = (Array.isArray(condition.value) ? condition.value : [condition.value]).map(
          String,
        );
        return { mode: condition.operator === 'not_equals' ? 'except' : 'only', ids };
      }
    }
  }
  return { mode: 'all', ids: [] };
};

const buildScopeRules = (mode, ids) =>
  mode === 'all'
    ? []
    : [[{ field: 'vertical', operator: mode === 'except' ? 'not_equals' : 'equals', value: ids }]];

const APPLIED_TO_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'only', label: 'Only' },
  { value: 'except', label: 'Except' },
];

const AppliedToControl = ({ block, verticals, onChange }) => {
  const { mode, ids } = getAppliedTo(block);
  const options = (verticals ?? []).map((vertical) => ({
    value: String(vertical.id),
    label: vertical.name,
  }));

  return (
    <div
      className="flex w-full items-start justify-end gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="shrink-0 pt-3.5 whitespace-nowrap text-xs text-muted-foreground">
        Applied to
      </span>
      <div className="w-32 shrink-0">
        <Select
          value={mode}
          onValueChange={(nextMode) => onChange(nextMode, nextMode === 'all' ? [] : ids)}
          options={APPLIED_TO_OPTIONS}
        />
      </div>
      <div className="w-80 shrink-0">
        {mode !== 'all' && (
          <MultiSelect
            value={ids}
            onValueChange={(nextIds) => onChange(mode, nextIds)}
            options={options}
            placeholder="Select verticals..."
            searchPlaceholder="Search verticals..."
            noResultsText="No verticals found."
            selectedCountText={(count) => `${count} vertical${count === 1 ? '' : 's'}`}
          />
        )}
      </div>
    </div>
  );
};

const BlockRow = ({
  block,
  locked,
  enabled,
  onToggle,
  onOpen,
  onDelete,
  deleteHint,
  hideEdit,
  rightControl,
}) => (
  <div
    className={`flex gap-3 py-2.5 pl-10 pr-4 transition-opacity ${
      rightControl ? 'items-start' : 'items-center'
    } ${!locked && !rightControl && !enabled ? 'opacity-50' : ''}`}
  >
    <button
      type="button"
      onClick={onOpen}
      className={`group min-w-0 cursor-pointer !bg-transparent p-0 text-left hover:!bg-transparent ${
        rightControl ? 'w-72 shrink-0' : 'flex-1'
      }`}
    >
      <span className="flex items-center gap-2">
        {locked && <Lock className="size-3 shrink-0 text-muted-foreground" />}
        <span className="truncate text-sm font-medium text-foreground group-hover:underline">
          {block.name}
        </span>
      </span>
      {block.description && (
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {block.description}
        </span>
      )}
    </button>
    {rightControl && <div className="min-w-0 flex-1">{rightControl}</div>}
    {!hideEdit && (
      <button
        type="button"
        onClick={onOpen}
        className={`rounded p-1.5 text-muted-foreground opacity-70 transition-colors hover:!bg-[#e2e8f0] hover:text-foreground hover:opacity-100 ${
          rightControl ? 'mt-2' : ''
        }`}
        aria-label={locked ? `View ${block.name}` : `Edit ${block.name}`}
      >
        {locked ? <Eye className="size-3.5" /> : <Pencil className="size-3.5" />}
      </button>
    )}
    {!locked && (onDelete ? (
      <button
        type="button"
        onClick={onDelete}
        className={`rounded p-1.5 text-muted-foreground opacity-70 transition-colors hover:!bg-red-50 hover:text-destructive hover:opacity-100 ${
          rightControl ? 'mt-2' : ''
        }`}
        aria-label={`Delete ${block.name}`}
      >
        <Trash2 className="size-3.5" />
      </button>
    ) : (
      <button
        type="button"
        disabled
        title={deleteHint}
        className="cursor-not-allowed rounded p-1.5 text-muted-foreground opacity-30"
        aria-label={deleteHint}
      >
        <Trash2 className="size-3.5" />
      </button>
    ))}
    {!locked && !rightControl && <ToggleSwitch checked={enabled} onCheckedChange={onToggle} />}
  </div>
);

const BlockDialog = ({ open, onOpenChange, block, locked, isNew, onSave }) => {
  const [draft, setDraft] = useState({ name: '', description: '', content: '' });

  useEffect(() => {
    if (open) {
      setDraft({
        name: block?.name ?? '',
        description: block?.description ?? '',
        content: block?.content ?? '',
      });
    }
  }, [open, block]);

  const onFieldChange = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="th w-[640px] max-w-[92vw] p-6">
        <DialogHeader className="mb-4">
          <DialogTitle>
            {isNew ? 'New Instruction Block' : locked ? 'Core Instruction' : 'Edit Instruction Block'}
          </DialogTitle>
        </DialogHeader>

        {locked ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg !border !border-solid !border-[#cbd5e1] !bg-[#f8fafc] px-4 py-3 text-sm text-muted-foreground">
              Core instructions come from the base prompt. They always run and cannot be edited or
              turned off.
            </div>
            <div>
              <p className="mb-1 text-sm font-medium text-foreground">{block?.name}</p>
              {block?.description && (
                <p className="text-xs text-muted-foreground">{block.description}</p>
              )}
            </div>
            <pre className="whitespace-pre-wrap rounded-lg !border !border-solid !border-[#e2e8f0] !bg-white p-4 font-mono text-sm text-foreground">
              {block?.content}
            </pre>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Input
              label="Name"
              value={draft.name}
              onChange={(e) => onFieldChange('name', e.target.value)}
              placeholder="e.g. hotel-amenities-tone"
            />
            <Input
              label="Description"
              value={draft.description}
              onChange={(e) => onFieldChange('description', e.target.value)}
            />
            <Textarea
              label="Content"
              value={draft.content}
              onChange={(e) => onFieldChange('content', e.target.value)}
              autoGrow={false}
              className="min-h-[220px] font-mono text-sm"
              placeholder="Instruction text appended to the prompt..."
            />
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {locked ? 'Close' : 'Cancel'}
          </Button>
          {!locked && (
            <Button onClick={() => onSave(draft)} disabled={!draft.name.trim()}>
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const GROUP_HEADER = 'px-4 !bg-[#f8fafc] hover:!bg-[#f1f5f9] transition-colors';

const InstructionBlocksSection = ({ scope, verticalName, verticals, onBlocksChange }) => {
  const isBase = scope === 'base';

  const [blocks, setBlocksState] = useState([]);
  const setBlocks = (updater) => {
    setBlocksState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      onBlocksChange?.(next);
      return next;
    });
  };
  const initialSetBlocksRef = useRef(setBlocks);
  const [loading, setLoading] = useState(true);
  const [enabledById, setEnabledById] = useState({});
  const [dialog, setDialog] = useState({ open: false, block: null, locked: false, isNew: false });
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await searchEntityRecords(ENTITY_NAME_BACK_OFFICE_INSTRUCTION_BLOCK, {
          pageNumber: 1,
          pageSize: 1000,
          ignoreLimit: true,
          orderBy: 'sortOrder',
          sortOrderAscending: true,
        });
        const list = Array.isArray(result?.data?.list) ? result.data.list : [];
        initialSetBlocksRef.current(list);
        setEnabledById(Object.fromEntries(list.map((block) => [block.id, !!block.isActive])));
      } catch (error) {
        console.error('InstructionBlocksSection: failed to load blocks', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const { baseBlocks, verticalBlocks } = useMemo(() => {
    if (isBase) return { baseBlocks: blocks, verticalBlocks: [] };
    return {
      baseBlocks: blocks.filter((block) => !hasVerticalRule(block)),
      verticalBlocks: blocks.filter((block) => referencesVertical(block, scope)),
    };
  }, [blocks, isBase, scope]);

  const persistBlockUpdate = async (blockId, changes) => {
    const existing = blocks.find((block) => block.id === blockId);
    if (!existing) return null;
    const saved = await saveBlockRequest({ ...existing, ...changes }, blockId);
    setBlocks((prev) => prev.map((block) => (block.id === blockId ? saved : block)));
    return saved;
  };

  const toggle = async (id) => {
    const next = !enabledById[id];
    setEnabledById((prev) => ({ ...prev, [id]: next }));
    try {
      await persistBlockUpdate(id, { isActive: next });
    } catch (error) {
      console.error('InstructionBlocksSection: toggle failed', error);
      setEnabledById((prev) => ({ ...prev, [id]: !next }));
      showErrorMessage(error?.message || 'Failed to update instruction block.');
    }
  };

  const onAppliedToChange = async (blockId, mode, ids) => {
    try {
      await persistBlockUpdate(blockId, { scopeRules: buildScopeRules(mode, ids) });
    } catch (error) {
      console.error('InstructionBlocksSection: applied-to change failed', error);
      showErrorMessage(error?.message || 'Failed to update instruction block.');
    }
  };

  const onCount = (list) => list.filter((block) => enabledById[block.id]).length;

  const openBlock = (block, locked = false) =>
    setDialog({ open: true, block, locked, isNew: false });

  const openNew = () => setDialog({ open: true, block: null, locked: false, isNew: true });

  const onConfirmDelete = async () => {
    if (deleteTarget) {
      try {
        await deleteBlockRequest(deleteTarget.id);
        setBlocks((prev) => prev.filter((block) => block.id !== deleteTarget.id));
        showSuccessMessage('Instruction block deleted.');
      } catch (error) {
        console.error('InstructionBlocksSection: delete failed', error);
        showErrorMessage(error?.message || 'Failed to delete instruction block.');
      }
    }
    setDeleteTarget(null);
  };

  const onDialogSave = async (draft) => {
    if (!/^[a-zA-Z0-9_-]+$/.test(draft.name)) {
      showErrorMessage('Name may only contain letters, numbers, hyphens and underscores — no spaces. It is referenced from prompts as {{block:name}}.');
      return;
    }
    if (!draft.content?.trim()) {
      showErrorMessage('Content is required.');
      return;
    }

    try {
      if (dialog.isNew) {
        const payload = {
          name: draft.name,
          description: draft.description,
          content: draft.content,
          contentTypeKeys: [],
          scopeRules: isBase
            ? []
            : [[{ field: 'vertical', operator: 'equals', value: [String(scope)] }]],
          sortOrder: 0,
          isActive: true,
        };
        const saved = await saveBlockRequest(payload);
        setBlocks((prev) => [...prev, saved]);
        setEnabledById((prev) => ({ ...prev, [saved.id]: !!saved.isActive }));
      } else {
        const existing = blocks.find((block) => block.id === dialog.block.id);
        const saved = await saveBlockRequest({ ...existing, ...draft }, dialog.block.id);
        setBlocks((prev) =>
          prev.map((block) => (block.id === dialog.block.id ? saved : block)),
        );
      }
      showSuccessMessage('Instruction block saved.');
      setDialog((prev) => ({ ...prev, open: false }));
    } catch (error) {
      console.error('InstructionBlocksSection: save failed', error);
      showErrorMessage(error?.message || 'Failed to save instruction block.');
    }
  };

  const renderGroup = ({ title, description, rightContent, rows, defaultOpen, emptyText }) => (
    <Collapse
      size="md"
      indicatorPosition="left"
      defaultOpen={defaultOpen}
      title={title}
      description={description}
      rightContent={rightContent}
      headerClassName={GROUP_HEADER}
      contentClassName="!pb-0 divide-y divide-[#f1f5f9]"
    >
      {rows.length ? (
        rows
      ) : (
        <div className="py-4 pl-10 pr-4 text-sm text-muted-foreground">{emptyText}</div>
      )}
    </Collapse>
  );

  return (
    <div className="mb-10 mt-2">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="mr-auto flex items-center gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
            Instruction Blocks
          </h3>
        </div>
        <Button variant="secondary" leftIcon={<Plus className="size-4" />} onClick={openNew}>
          Add Block
        </Button>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        {isBase
          ? 'Extra instructions appended to every generation prompt.'
          : `Extra instructions applied when generating content for ${verticalName ?? 'this vertical'}.`}
      </p>

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Loading instruction blocks...
        </div>
      ) : (
        <div className="overflow-hidden !rounded-xl !border !border-solid !border-[#cbd5e1] !bg-white !shadow-sm [&>*+*]:!border-t [&>*+*]:!border-solid [&>*+*]:!border-[#e2e8f0]">
          {isBase &&
            renderGroup({
              title: 'Core',
              description: 'built into the base prompt',
              rightContent: (
                <span className="inline-flex items-center gap-1">
                  <Lock className="size-3" />
                  always on
                </span>
              ),
              rows: CORE_BLOCKS.map((block) => (
                <BlockRow
                  key={block.id}
                  block={block}
                  locked
                  enabled
                  onToggle={() => {}}
                  onOpen={() => openBlock(block, true)}
                />
              )),
              defaultOpen: false,
            })}

          {renderGroup({
            title: isBase ? 'Additional' : 'From base',
            description: isBase
              ? 'configurable — apply to all verticals'
              : 'inherited — toggle per vertical',
            rightContent: isBase
              ? `${baseBlocks.length} block${baseBlocks.length === 1 ? '' : 's'}`
              : `${onCount(baseBlocks)} of ${baseBlocks.length} on`,
            rows: baseBlocks.map((block) => (
              <BlockRow
                key={block.id}
                block={block}
                enabled={!!enabledById[block.id]}
                onToggle={() => toggle(block.id)}
                onOpen={() => openBlock(block)}
                onDelete={isBase ? () => setDeleteTarget(block) : null}
                deleteHint="Applied from base — can only be deleted from Base Prompts"
                hideEdit={!isBase}
                rightControl={
                  isBase ? (
                    <AppliedToControl
                      block={block}
                      verticals={verticals}
                      onChange={(mode, ids) => onAppliedToChange(block.id, mode, ids)}
                    />
                  ) : null
                }
              />
            )),
            defaultOpen: true,
            emptyText: 'No additional instruction blocks yet.',
          })}

          {!isBase &&
            renderGroup({
              title: `${verticalName ?? 'Vertical'} specific`,
              description: 'only applied for this vertical',
              rightContent: `${onCount(verticalBlocks)} of ${verticalBlocks.length} on`,
              rows: verticalBlocks.map((block) => (
                <BlockRow
                  key={block.id}
                  block={block}
                  enabled={!!enabledById[block.id]}
                  onToggle={() => toggle(block.id)}
                  onOpen={() => openBlock(block)}
                  onDelete={() => setDeleteTarget(block)}
                />
              )),
              defaultOpen: true,
              emptyText: `No ${verticalName ?? 'vertical'}-specific blocks yet — use Add Block to create one.`,
            })}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={onConfirmDelete}
        title="Delete instruction block?"
        description={`This permanently deletes "${deleteTarget?.name ?? ''}"${
          isBase ? ' for all verticals that inherit it.' : ` for ${verticalName ?? 'this vertical'}.`
        }`}
        confirmLabel="Delete"
        confirmVariant="danger"
      />

      <BlockDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        block={dialog.block}
        locked={dialog.locked}
        isNew={dialog.isNew}
        onSave={onDialogSave}
      />
    </div>
  );
};

export default InstructionBlocksSection;
